import { SportsApiClient, LeaguesApiClient, EventsApiClient, MarketsApiClient, BreedsApiClient, RacesApiClient, SearchApiClient, RegionsApiClient, TracksApiClient } from "./BFFGatewayClient";
import { GlobalConfig } from "../configuration";
import { BFFGatewayConfiguration } from "./BFFGatewayConfiguration";
import { AjaxService } from "sb-helpers";
/**
 * BFFGateway class provides interface for communication between BFF service and blocks.
 *
 * sbBffIntegration.GlobalConfig object needs to be inited before using BFFGateway
 * as base URL for BFF is present from config.
 *
 * example usage:
 * var gateway = new sbBffIntegration.BFFGateway();
 * gateway.getSports({ ... });
 *
 */
/* istanbul ignore next */
var BFFGateway = /** @class */ (function () {
    function BFFGateway() {
        this.config = new BFFGatewayConfiguration({
            accessToken: GlobalConfig
                .getAuthorization()
                .token,
            locale: GlobalConfig.apiConfigs.locale,
            basePath: GlobalConfig.apiConfigs.bffApiUrl
        });
        var service = new AjaxService();
        this.sportsApiClient = new SportsApiClient(this.config, service);
        this.leaguesApiClient = new LeaguesApiClient(this.config, service);
        this.eventsApiClient = new EventsApiClient(this.config, service);
        this.marketsApiClient = new MarketsApiClient(this.config, service);
        this.breedsApiClient = new BreedsApiClient(this.config, service);
        this.racesApiClient = new RacesApiClient(this.config, service);
        this.regionsApiClient = new RegionsApiClient(this.config, service);
        this.tracksApiClient = new TracksApiClient(this.config, service);
        this.searchApiClient = new SearchApiClient(this.config, service);
    }
    /**
     * Get BFF request configuration from GlobalConfig
     * @return token from GlobalConfig
     */
    BFFGateway.prototype.getGlobalConfigToken = function () {
        return GlobalConfig
            .getAuthorization()
            .token;
    };
    //builder constructor for BffGateway with blockId parameter
    /**
     *
     * @param blockId
     */
    BFFGateway.prototype.withBlockId = function (blockId) {
        this.config.blockId = blockId;
        return this;
    };
    //Event endpoints
    /**
     * Request events by sport id
     * @param request EventsBySportIdRequest
     */
    BFFGateway.prototype.getEventsBySportId = function (request) {
        return this
            .eventsApiClient
            .refreshToken(this.getGlobalConfigToken())
            .getBySportId(request);
    };
    /**
     * Request events by event ids
     * @param request EventsByEventIdRequest
     */
    BFFGateway.prototype.getEventsByEventId = function (request) {
        return this
            .eventsApiClient
            .refreshToken(this.getGlobalConfigToken())
            .getByEventsId(request);
    };
    /**
     *
     * @param request EventsByRegionId
     */
    BFFGateway.prototype.getEventsByRegionId = function (request) {
        return this
            .eventsApiClient
            .refreshToken(this.getGlobalConfigToken())
            .getByRegionId(request);
    };
    /**
     * Request events by league ids
     * @param request EventsByLeagueIdRequest
     */
    BFFGateway.prototype.getEventsByLeagueId = function (request) {
        return this
            .eventsApiClient
            .refreshToken(this.getGlobalConfigToken())
            .getByLeaguesId(request);
    };
    /**
     * Request events by event ids or by league ids
     * @param request EventsByEventIdOrByLeagueIdRequest
     */
    BFFGateway.prototype.getEventsByEventIdOrByLeagueId = function (request) {
        this
            .eventsApiClient
            .refreshToken(this.getGlobalConfigToken());
        return this
            .eventsApiClient
            .getByEventIdOrByLeagueId(request);
    };
    /**
     * Request events from top leagues by sport id
     * @param request EventsFromTopLeaguesBySportIdRequest
     */
    BFFGateway.prototype.getEventsFromTopLeaguesBySportId = function (request) {
        return this
            .eventsApiClient
            .refreshToken(this.getGlobalConfigToken())
            .getFromTopLeaguesBySportId(request);
    };
    /**
     * Request personalized live events based on event id
     * @param request PersonalizedLiveEventsRequest
     */
    BFFGateway.prototype.getPersonalizedEvents = function (request) {
        return this
            .eventsApiClient
            .refreshToken(this.getGlobalConfigToken())
            .getPersonalizedEvents(request);
    };
    /**
     * Request events by eventIds, marketIds and selectionIds
     * @param request EventsByEventIdForBetSlipRequest
     */
    BFFGateway.prototype.getEventsByIdForBetslip = function (request) {
        return this
            .eventsApiClient
            .refreshToken(this.getGlobalConfigToken())
            .getByIdForBetslip(request);
    };
    //Markets endpoints
    /**
     * Request markets by event id
     * @param request MarketsByEventIdRequest
     */
    BFFGateway.prototype.getMarketsByEventId = function (request) {
        return this
            .marketsApiClient
            .refreshToken(this.getGlobalConfigToken())
            .getByEventId(request);
    };
    //Leagues endpoints
    /**
     * Request all leagues by sport ids
     * @param request LeaguesBySportIdRequest
     */
    BFFGateway.prototype.getLeaguesBySportId = function (request) {
        return this
            .leaguesApiClient
            .refreshToken(this.getGlobalConfigToken())
            .getBySportId(request);
    };
    //Leagues endpoints
    /**
     * Request all leagues by sport ids
     * @param request LeaguesByLeagueIdRequest
     */
    BFFGateway.prototype.getLeaguesByLeagueId = function (request) {
        return this
            .leaguesApiClient
            .refreshToken(this.getGlobalConfigToken())
            .getLeaguesByLeagueId(request);
    };
    /**
     * Request all top leagues by sport id
     * @param request TopLeaguesBySportIdRequest
     */
    BFFGateway.prototype.getTopLeaguesBySportId = function (request) {
        return this
            .leaguesApiClient
            .refreshToken(this.getGlobalConfigToken())
            .getTopLeaguesBySportId(request);
    };
    //Sports endpoints
    /**
     * Request all sports
     * @param request SportsRequest
     */
    BFFGateway.prototype.getSports = function (request) {
        return this
            .sportsApiClient
            .refreshToken(this.getGlobalConfigToken())
            .getAllSports(request);
    };
    /**
     * Get sports grouped by Date from BFF
     * @param {request} SportsGroupedByDateRequest
     * @throws {RequiredError}
     * @memberof SportsApiClient
     */
    BFFGateway.prototype.getSportsGroupedByDate = function (request) {
        return this.sportsApiClient
            .refreshToken(this.getGlobalConfigToken())
            .getSportsGroupedByDate(request);
    };
    /**
     * Fetch events count by tag from BFF
     * @param {request} EventsCountByTagRequest
     * @throws {RequiredError}
     * @memberof EventsApiClient
     */
    BFFGateway.prototype.getCountByTag = function (request) {
        this.sportsApiClient.refreshToken(this.getGlobalConfigToken());
        return this.eventsApiClient.getCountByTag(request);
    };
    /**
     * Request all breeds
     */
    BFFGateway.prototype.getBreeds = function () {
        return this.breedsApiClient
            .refreshToken(this.getGlobalConfigToken())
            .getBreeds();
    };
    //Races endpoints
    /**
      * Get a race by race id from BFF
      * @param {string} raceId id of a race
      * @throws {RequiredError}
      * @memberof RacesApiClient
      */
    BFFGateway.prototype.getRacesById = function (raceId) {
        return this.racesApiClient
            .refreshToken(this.getGlobalConfigToken())
            .getById(raceId);
    };
    /**
     * Get races by league ids from BFF
     * @param {string[]} leagueIds leagueIds to get races from
     * @param {number} paginationTop index of last race
     * @param {number} paginationSkip number races to skip
     * @throws {RequiredError}
     * @memberof RacesApiClient
     */
    BFFGateway.prototype.getRaces = function (request) {
        return this.racesApiClient
            .refreshToken(this.getGlobalConfigToken())
            .getRaces(request);
    };
    /**
     * Get all races with no participants and tote details from BFF
     * @throws {RequiredError}
     * @memberof RacesApiClient
     */
    BFFGateway.prototype.getRacesCompact = function () {
        return this.racesApiClient
            .refreshToken(this.getGlobalConfigToken())
            .getRacesCompact();
    };
    /**
     * Get all regions from BFF
     * @throws {RequiredError}
     * @memberof RegionsApiClient
     */
    BFFGateway.prototype.getRegions = function () {
        return this.regionsApiClient
            .refreshToken(this.getGlobalConfigToken())
            .getRegions();
    };
    /**
     * Get all tracks from BFF
     * @throws {RequiredError}
     * @memberof RegionsApiClient
     */
    BFFGateway.prototype.getTracks = function () {
        return this.tracksApiClient
            .refreshToken(this.getGlobalConfigToken())
            .getTracks();
    };
    /**
     * Search events and leagues from BFF
     * @throws {RequiredError}
     * @memberof SearchApiClient
     */
    BFFGateway.prototype.search = function (query) {
        return this.searchApiClient
            .refreshToken(this.getGlobalConfigToken())
            .search(query);
    };
    return BFFGateway;
}());
export { BFFGateway };
//# sourceMappingURL=BFFGateway.js.map